import { Title } from '@solidjs/meta'
import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import FabMenu from '~/components/FabMenu'
import Icon from '~/components/Icon'
import InfiniteList from '~/components/InfiniteList'
import Input from '~/components/Input'
import SwipeAction from '~/components/SwipeAction'
import Table from '~/components/Table'
import Tabs from '~/components/Tabs'
import { useIsMobile } from '~/hooks/useIsMobile'
import { type Path, useNavigate } from '~/router'
import { type InventoryMovementType, inventoryService, type ProductType, toNumber, variantLabel } from '~/services/product.service'
import { resolveError } from '~/utils/error'

import { movementColor, movementLabel } from '~/utils/status'

/**
 * Qué hay y qué se ha movido, mirando toda la empresa a la vez. El detalle y el
 * registro de movimientos viven en la ficha de cada producto: aquí interesa lo
 * contrario, ver de un vistazo qué está por debajo del mínimo.
 */
const InventarioPage: Component = () => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [tab, setTab] = createSignal('existencias')
  const [onlyLow, setOnlyLow] = createSignal(false)

  // La fuente va envuelta en un objeto a propósito: `createResource` no ejecuta
  // la carga cuando su fuente es falsy, y con el booleano suelto la pantalla se
  // quedaba vacía mientras el filtro estuviera desactivado.
  const [stock, { refetch }] = createResource(
    () => ({ low: onlyLow() }),
    async ({ low }) => {
      try {
        return await inventoryService.list(low)
      } catch {
        return { items: [], meta: { total: 0, lowStock: 0, sinExistencias: 0 } }
      }
    }
  )
  const [movements, { refetch: refetchMovements }] = createResource(async () => {
    try {
      return await inventoryService.movements()
    } catch {
      return []
    }
  })

  const [filterSearch, setFilterSearch] = createSignal('')
  const [filterMovement, setFilterMovement] = createSignal('')

  const filteredStock = createMemo(() => {
    const list = stock()?.items ?? []
    const q = filterSearch().trim().toLowerCase()
    if (!q) return list
    return list.filter(p => [p.name, p.sku, p.key, p.category?.label].filter(Boolean).join(' ').toLowerCase().includes(q))
  })

  const filteredMovements = createMemo(() => {
    const list = movements() ?? []
    const q = filterMovement().trim().toLowerCase()
    if (!q) return list
    return list.filter(m => `${m.product?.name ?? ''} ${m.reason ?? ''} ${m.reference ?? ''}`.toLowerCase().includes(q))
  })

  const meta = () => stock()?.meta ?? { total: 0, lowStock: 0, sinExistencias: 0 }

  return (
    <>
      <Title>Inventario | Florez Jaramillo</Title>

      <div class="flex gap-4 overflow-x-auto hide-scrollbar mb-6 sm:grid sm:grid-cols-3 sm:overflow-visible">
        <Card class="min-w-40 sm:min-w-0">
          <Card.Body>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-2xl font-bold text-foreground">{meta().total}</p>
                <p class="text-xs text-foreground-subtle">Productos con inventario</p>
              </div>
              <Icon icon="material-symbols-light:warehouse-outline" size={24} class="text-primary" />
            </div>
          </Card.Body>
        </Card>
        <Card class="min-w-40 sm:min-w-0">
          <Card.Body>
            <div class="flex items-center justify-between">
              <div>
                <p class={`text-2xl font-bold ${meta().lowStock > 0 ? 'text-danger' : 'text-foreground'}`}>{meta().lowStock}</p>
                <p class="text-xs text-foreground-subtle">Bajo mínimos</p>
              </div>
              <Icon icon="material-symbols-light:warning-outline" size={24} class={meta().lowStock > 0 ? 'text-danger' : 'text-foreground-subtle'} />
            </div>
          </Card.Body>
        </Card>
        <Card class="min-w-40 sm:min-w-0">
          <Card.Body>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-2xl font-bold text-foreground">{meta().sinExistencias}</p>
                <p class="text-xs text-foreground-subtle">Sin existencias</p>
              </div>
              <Icon icon="material-symbols-light:inbox-outline" size={24} class="text-warning" />
            </div>
          </Card.Body>
        </Card>
      </div>

      <Show when={stock.error}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(stock.error)}</div>
      </Show>

      <Card>
        <Card.Body>
          <Tabs
            items={[
              { id: 'existencias', label: 'Existencias', icon: 'material-symbols-light:inventory-2-outline' },
              { id: 'movimientos', label: 'Movimientos', icon: 'material-symbols-light:history' }
            ]}
            activeTab={tab()}
            onChange={setTab}
            variant="underline"
          />

          <div class="pt-5">
            <Show when={tab() === 'existencias'}>
              <Show when={!isMobile()}>
                <Table<ProductType>
                  data={filteredStock()}
                  keyField="id"
                  loading={stock.loading}
                  emptyMessage={onlyLow() ? 'Nada por debajo del mínimo' : 'Ningún producto lleva inventario todavía'}
                  headerActions={
                    <div class="flex items-center gap-2">
                      <Input
                        label="Buscar"
                        value={filterSearch()}
                        onInput={e => setFilterSearch(e.currentTarget.value)}
                        placeholder="Producto, SKU o categoría..."
                        startIcon="material-symbols-light:search"
                        class="min-w-48"
                      />
                      <Button variant="soft" color="default" onClick={() => refetch()} prependIcon="material-symbols-light:refresh" />
                      <Button
                        variant={onlyLow() ? 'contained' : 'soft'}
                        color={onlyLow() ? 'danger' : 'default'}
                        onClick={() => setOnlyLow(v => !v)}
                        prependIcon="material-symbols-light:warning-outline"
                      >
                        {onlyLow() ? 'Viendo sólo los que faltan' : 'Ver sólo bajo mínimos'}
                      </Button>
                    </div>
                  }
                  onRowClick={p => navigate(`/admin/servicios/${p.id}?tab=inventario` as Path)}
                  columns={[
                    {
                      header: 'Producto',
                      sticky: 'left' as const,
                      minWidth: 240,
                      accessor: p => (
                        <div class="min-w-0">
                          <div class="font-medium text-foreground truncate">{p.name}</div>
                          <div class="text-xs text-foreground-subtle truncate">{p.sku || p.category?.label || p.key}</div>
                        </div>
                      )
                    },
                    {
                      header: 'Existencias',
                      accessor: p => (
                        <span class={p.lowStock ? 'font-medium text-danger' : 'text-foreground'}>
                          {toNumber(p.stockTotal ?? p.stock).toLocaleString('es-CO')} {p.unit}
                        </span>
                      )
                    },
                    { header: 'Mínimo', accessor: p => toNumber(p.minStock).toLocaleString('es-CO') },
                    {
                      header: 'Variantes',
                      accessor: p => (
                        <Show when={p.variants?.length} fallback={<span class="text-foreground-subtle">—</span>}>
                          <div class="flex flex-wrap gap-1">
                            <For each={p.variants}>
                              {v => (
                                <Badge color={toNumber(v.stock) <= toNumber(v.minStock) && toNumber(v.minStock) > 0 ? 'danger' : 'default'} variant="soft" size="sm">
                                  {variantLabel(v)}: {toNumber(v.stock).toLocaleString('es-CO')}
                                </Badge>
                              )}
                            </For>
                          </div>
                        </Show>
                      )
                    },
                    {
                      header: 'Estado',
                      accessor: p =>
                        p.lowStock ? (
                          <Badge color="danger">Reponer</Badge>
                        ) : toNumber(p.stockTotal ?? p.stock) <= 0 ? (
                          <Badge color="warning">Sin existencias</Badge>
                        ) : (
                          <Badge color="success">Correcto</Badge>
                        )
                    }
                  ]}
                  actions={p => (
                    <Button
                      size="sm"
                      variant="soft"
                      color="info"
                      icon="material-symbols-light:add-circle-outline"
                      tooltip="Registrar movimiento"
                      onClick={e => {
                        e.stopPropagation()
                        navigate(`/admin/servicios/${p.id}?tab=inventario` as Path)
                      }}
                    />
                  )}
                />
              </Show>

              <Show when={isMobile()}>
                <InfiniteList<ProductType>
                  items={filteredStock()}
                  keyField={p => p.id}
                  hasMore={false}
                  loading={stock.loading}
                  onLoadMore={() => {}}
                  onRefresh={async () => {
                    await refetch()
                    await refetchMovements()
                  }}
                  emptyMessage={<p class="py-10 text-center text-foreground-subtle">{onlyLow() ? 'Nada por debajo del mínimo' : 'Ningún producto lleva inventario todavía'}</p>}
                  endMessage={<span>Has llegado al final del inventario</span>}
                  class="space-y-3"
                  renderItem={p => (
                    <SwipeAction
                      itemId={p.id}
                      rememberPosition
                      onClick={() => navigate(`/admin/servicios/${p.id}?tab=inventario` as Path)}
                      leftOptions={[
                        {
                          icon: 'material-symbols-light:visibility-outline',
                          label: 'Ver',
                          color: 'info',
                          action: () => navigate(`/admin/servicios/${p.id}?tab=inventario` as Path)
                        }
                      ]}
                      rightOptions={[
                        {
                          icon: 'material-symbols-light:add-circle-outline',
                          label: 'Movimiento',
                          color: 'primary',
                          action: () => navigate(`/admin/servicios/${p.id}?tab=inventario` as Path)
                        }
                      ]}
                    >
                      <Card class="!rounded-lg">
                        <Card.Body class="space-y-2">
                          <div class="flex items-center justify-between gap-2">
                            <p class="font-medium text-foreground truncate">{p.name}</p>
                            {p.lowStock ? (
                              <Badge color="danger">Reponer</Badge>
                            ) : toNumber(p.stockTotal ?? p.stock) <= 0 ? (
                              <Badge color="warning">Sin existencias</Badge>
                            ) : (
                              <Badge color="success">Correcto</Badge>
                            )}
                          </div>
                          <p class="text-xs text-foreground-subtle truncate">{p.sku || p.category?.label || p.key}</p>
                          <div class="flex items-center gap-3 text-sm">
                            <span class={p.lowStock ? 'font-semibold text-danger' : 'font-semibold text-foreground'}>
                              {toNumber(p.stockTotal ?? p.stock).toLocaleString('es-CO')} {p.unit}
                            </span>
                            <span class="text-foreground-subtle">mín. {toNumber(p.minStock).toLocaleString('es-CO')}</span>
                          </div>
                          <Show when={p.variants?.length}>
                            <div class="flex flex-wrap gap-1">
                              <For each={p.variants}>
                                {v => (
                                  <Badge color={toNumber(v.stock) <= toNumber(v.minStock) && toNumber(v.minStock) > 0 ? 'danger' : 'default'} variant="soft" size="sm">
                                    {variantLabel(v)}: {toNumber(v.stock).toLocaleString('es-CO')}
                                  </Badge>
                                )}
                              </For>
                            </div>
                          </Show>
                        </Card.Body>
                      </Card>
                    </SwipeAction>
                  )}
                />
              </Show>
            </Show>

            <Show when={tab() === 'movimientos'}>
              <Show when={!isMobile()}>
                <Table<InventoryMovementType>
                  data={filteredMovements()}
                  keyField="id"
                  loading={movements.loading}
                  emptyMessage="Todavía no se ha movido nada"
                  headerActions={
                    <div class="flex items-center gap-2">
                      <Input
                        label="Buscar"
                        value={filterMovement()}
                        onInput={e => setFilterMovement(e.currentTarget.value)}
                        placeholder="Producto o motivo..."
                        startIcon="material-symbols-light:search"
                        class="min-w-48"
                      />
                    </div>
                  }
                  columns={[
                    { header: 'Fecha', accessor: m => new Date(m.createdAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) },
                    {
                      header: 'Producto',
                      sticky: 'left' as const,
                      minWidth: 220,
                      accessor: m => (
                        <div class="min-w-0">
                          <div class="font-medium text-foreground truncate">{m.product?.name ?? '—'}</div>
                          <Show when={m.variant}>
                            <div class="text-xs text-foreground-subtle truncate">{variantLabel(m.variant!)}</div>
                          </Show>
                        </div>
                      )
                    },
                    {
                      header: 'Tipo',
                      accessor: m => (
                        <Badge color={movementColor[m.type]} variant="soft">
                          {movementLabel[m.type]}
                        </Badge>
                      )
                    },
                    {
                      header: 'Cantidad',
                      accessor: m => {
                        const qty = toNumber(m.quantity)
                        // El ajuste ya trae su propio signo; los otros lo reciben del tipo.
                        if (m.type === 'adjust') return `${qty > 0 ? '+' : ''}${qty.toLocaleString('es-CO')}`
                        return `${m.type === 'out' ? '-' : '+'}${Math.abs(qty).toLocaleString('es-CO')}`
                      }
                    },
                    { header: 'Saldo', accessor: m => toNumber(m.balance).toLocaleString('es-CO') },
                    { header: 'Motivo', accessor: m => m.reason || '—' },
                    { header: 'Quién', accessor: m => m.user?.name ?? '—' }
                  ]}
                />
              </Show>

              <Show when={isMobile()}>
                <InfiniteList<InventoryMovementType>
                  items={filteredMovements()}
                  keyField={m => m.id}
                  hasMore={false}
                  loading={movements.loading}
                  onLoadMore={() => {}}
                  onRefresh={async () => {
                    await refetchMovements()
                    await refetch()
                  }}
                  emptyMessage={<p class="py-10 text-center text-foreground-subtle">Todavía no se ha movido nada</p>}
                  endMessage={<span>Has llegado al final de los movimientos</span>}
                  class="space-y-3"
                  renderItem={m => {
                    const qty = toNumber(m.quantity)
                    const signo = m.type === 'adjust' ? (qty > 0 ? '+' : '') : m.type === 'out' ? '-' : '+'
                    const cantidad = m.type === 'adjust' ? `${signo}${qty.toLocaleString('es-CO')}` : `${signo}${Math.abs(qty).toLocaleString('es-CO')}`
                    return (
                      <Card class="!rounded-lg">
                        <Card.Body class="space-y-1.5">
                          <div class="flex items-center justify-between gap-2">
                            <p class="font-medium text-foreground truncate">{m.product?.name ?? '—'}</p>
                            <Badge color={movementColor[m.type]} variant="soft" class="shrink-0">
                              {movementLabel[m.type]}
                            </Badge>
                          </div>
                          <Show when={m.variant}>
                            <p class="text-xs text-foreground-subtle truncate">{variantLabel(m.variant!)}</p>
                          </Show>
                          <div class="flex items-center justify-between text-sm">
                            <span class={qty < 0 ? 'font-semibold text-danger' : 'font-semibold text-success'}>{cantidad}</span>
                            <span class="text-foreground-subtle">saldo {toNumber(m.balance).toLocaleString('es-CO')}</span>
                            <span class="text-xs text-foreground-subtle">{new Date(m.createdAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</span>
                          </div>
                          <Show when={m.reason || m.user}>
                            <p class="text-xs text-foreground-subtle truncate">{[m.reason, m.user?.name].filter(Boolean).join(' · ') || '—'}</p>
                          </Show>
                        </Card.Body>
                      </Card>
                    )
                  }}
                />
              </Show>
            </Show>
          </div>
        </Card.Body>
      </Card>

      {/* FAB móvil: el toggle de bajo mínimos en pantallas pequeñas */}
      <div class="sm:hidden">
        <FabMenu
          items={[
            {
              label: onlyLow() ? 'Viendo sólo bajo mínimos' : 'Ver sólo bajo mínimos',
              icon: 'material-symbols-light:warning-outline',
              onClick: () => setOnlyLow(v => !v)
            }
          ]}
        />
      </div>
    </>
  )
}

export default InventarioPage
